import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FreeSearchParameters } from '@app/core/entities/free-search-parameters.class';
import { Filter, FilterTypes, QueryParameters, SortField, WrapFilter } from '@app/core/entities/query-parameters.class';
import { ApiService } from '@app/core/services/api/api.service';
import { UtilService } from '@app/core/services/utils/util.service';
import { OrderCounterTypes, WSEventData } from '@app/core/services/web-socket/web-socket.model';
import { WebsocketSubscriberService } from '@app/core/services/websocket-subscriber/websocket.subscriber.service';
import { ApproveSplitOrderComponent } from '@app/features/side-panel/components/order-details.component/components/approve-split-order/approve-split-order.component';
import { EditOrderPackageComponent } from '@app/features/side-panel/components/order-details.component/components/order-package/edit-order-package/edit-order-package.component';
import { SidePanelService } from '@app/features/side-panel/side-panel.service';
import {
  DeliveryPromise,
  DeliveryPromiseLabel,
  FlaggedStatuses,
  Order,
  OrderFixStatuses,
  PaymentInvalidStatuses,
  PaymentValidStatuses,
} from '@app/services/order/order.model';
import * as _ from 'lodash';
import { OrderService } from '@app/services/order/order.service';
import { Shop } from '@app/services/shops/shop.model';
import { ShopService } from '@app/services/shops/shop.service';
import { TableService } from '@app/services/table/table.service';
import { UserService } from '@app/services/user/user.service';
import { AddTagsComponent } from '@app/sharedModule/components/add-tags/add-tags.component';
import { ChangeLocationDialogComponent } from '@app/sharedModule/components/change-location-dialog/change-location-dialog.component';
import { DeleteOrderDialogComponent } from '@app/sharedModule/components/delete-order-dialog/delete-order-dialog.component';
import { NewuiSearchAutocompleteComponent } from '@app/sharedModule/components/newui-search-autocomplete/newui-search-autocomplete.component';
import {
  ColumnSettings,
  ColumnTypes,
  GroupAction,
  RowAction,
  TableFilterType,
  tableMessages,
  ColumnName,
  ValidationStatuses,
} from '@app/sharedModule/models/table-settings.model';
import { Store } from '@ngrx/store';
import { ManualVerifyDialog } from '@pages/workspace/verify/dialog/manual-verify/manual-verify.dialog';
import { IShopMetadata } from '@pdqdev/pdq-shared/dist/interfaces/IShopMetadata';
import { TranslatedFulfillmentStatus, TranslatedPaymentStatus } from '@pdqdev/pdq-shared/dist/OrderData';
import { CookieService } from 'ngx-cookie-service';
import { asyncScheduler, EMPTY, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap, takeUntil, throttleTime } from 'rxjs/operators';
import {
  createStepFilter,
  formatBillTo,
  formatMultiParcelBoxName,
  formatShippingAddressString,
  formatShippingNameString,
  TABLES,
  TimeUnits,
  initColumnsOptionsByShop,
  modifyDateRangeValuesByColumn,
  getDeliveryPromise,
} from 'src/utils';
import { SplitOrderComponent } from '../../../sharedModule/components/split-order/split-order.component';
import { PdqToastComponent } from '../../../storybook/components/toast/toast.component';
import { ToastService } from '../../../storybook/services/toast/toast.service';
import { getAdditionalGroupActionsByShop, getAdditionalRowActionsByShop } from '../../checkout-app/utils';
import { DownloadorderdialogComponent } from '../assign/dialogs/downloadorderdialog/downloadorderdialog.component';
import { previewColumnsConfig } from './configs/table.config';
import { PreviewOrdersActions } from './store/actions';
import { PreviewOrdersSelectors } from './store/selectors';
import { OrderOperation, OrderStatus, WeightUnitEnum } from '@pdqdev/pdq-shared/dist/entities';
import { AutomationRulesReducer } from '@app/features/automation-rules/reducers';
import { TemplateData } from '@app/features/automation-rules/reducers/automation-rules.reducer';
import { getOrderSplitIndicesString, isSameSplitIndex } from '@utils/order-split-utils';
import { getDefaultSortField } from '@utils/shop-utils';
import { MergeOrdersComponent } from '@app/sharedModule/components/merge-orders/merge-orders.component';
import { HolddialogComponent } from '../assign/dialogs/holddialog/holddialog.component';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { PrintDocumentType } from '@utils/print-utils';
import { PrintService } from '@app/services/print/print.service';
import { KeyShortCutService } from '@services/key-shortcut/key-shortcut.service';
import { OrdersInProcess } from '@pdqdev/pdq-shared/dist/interfaces';
import { BoxService } from '@services/boxes/box.service';
import { getBoxUpdateFailureMessage, getBoxUpdateSuccessMessage } from '@utils/parcel.utils';
import { ExportOptionType } from '@utils/export-utils';
import { assignColorCodingToRows } from '@utils/color-coding.utils';
import { DeliverCursorPaginationService } from '@app/services/deliver-cursor-pagination/deliver-cursor-pagination.service';
import { DeliverCursorPagination } from '@app/services/deliver-cursor-pagination/deliver-cursor-pagination.model';
import { VerifyCursorPaginationService } from './services/verify-cursor-pagination.service';
import { PaginationCursorAction } from '@app/storybook/layouts/components/footer/pagination/pagination.component';

@Component({
  selector: 'app-preview',
  templateUrl: './preview.component.html',
  styleUrls: ['./preview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  providers: [{ provide: DeliverCursorPaginationService, useClass: VerifyCursorPaginationService }],
})
export class PreviewComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('input') input: ElementRef;
  @ViewChild(NewuiSearchAutocompleteComponent)
  searchAutocompleteRef: NewuiSearchAutocompleteComponent;
  readonly COLUMN_OPTIONS: ColumnSettings[] = previewColumnsConfig.filter((column: ColumnSettings) => column.canBeHide);
  readonly TABLES = TABLES;
  readonly FilterTypes = FilterTypes;
  private destroyed$: Subject<any> = new Subject();
  private isFiltersOnActiveMode: boolean = false;
  private shop: Shop;
  countAllOrders = 0;
  countFixOrders = 0;
  countValidatedOrders = 0;
  assignLoader = false;
  ordersPerPage = 50;
  columns: ColumnSettings[] = [...previewColumnsConfig];
  rowActions = [RowAction.RE_SYNC_ORDER, RowAction.DELETE_ORDER];
  sidePanelPrintActions = [
    PrintDocumentType.PACKING_SLIPS,
    PrintDocumentType.BROCHURE,
    PrintDocumentType.PICK_LIST,
    PrintDocumentType.CARD_ISLE,
  ];
  tablePrintActions = [
    PrintDocumentType.PICK_LIST,
    PrintDocumentType.PACKING_SLIPS,
    PrintDocumentType.BROCHURE,
    PrintDocumentType.CARD_ISLE,
  ];
  groupActions: GroupAction[];
  totalCountOrders: number;
  totalPages = 1;
  activePage = 1;
  allPages = {};
  selectedOrders = [];
  selectedValidOrders = [];
  isSyncingOrders: boolean;
  selectedColumns: string[];
  deliveryPromise: DeliveryPromise;
  filter = 'all';
  filterActive = false;
  showGreenFont = false;
  loading = false;
  temp = [];
  rows = [];
  searchToggleButtons = false;
  freeSearchInputControl = new UntypedFormControl();
  showFreeSearchSuggestions = false;
  showFreeSearchClearButton = false;
  freeSearchValue: any = '';
  freeSearchSelectedField = 'orderNumber';
  searchResults = [];
  isBottomScrolled: boolean = false;
  isLoadedInitialData = false;
  screenTitle = 'Verify new orders';
  shortcutScope = 'verify-table';
  keyboardShortcuts = [];

  deliverCursorPagination = inject(
    DeliverCursorPaginationService,
  ) as DeliverCursorPaginationService<DeliverCursorPagination.VerifyTabCounters>;
  useCursorPagination = this.deliverCursorPagination.useCursorPagination;
  cappedAllLabel = computed(() => this.getCappedCounterLabel(FilterTypes.LABEL_ALL));
  cappedVerifiedLabel = computed(() => this.getCappedCounterLabel(FilterTypes.LABEL_VERIFIED, 'validated'));
  cappedFixLabel = computed(() => this.getCappedCounterLabel(FilterTypes.LABEL_FLAGGED, 'fix'));
  focusedOrderId: string | null = null;
  isOrdersUpdating;
  disableGroupActions;
  shopLocations;
  shopBoxes;
  isCheckoutOnlyShop;
  disableQuickflows;

  private orderCounterSubscription$: Subscription;
  private orders$ = this.store.select(PreviewOrdersSelectors.selectOrders);
  private ordersSubscription$: Subscription;
  private totalCountOrders$ = this.store.select(PreviewOrdersSelectors.selectTotalCountOrders);
  private totalCountOrdersSubscription$: Subscription;
  private ordersLoading$ = this.store.select(PreviewOrdersSelectors.selectOrdersLoading);
  private templateDataChanged$ = this.store.select(AutomationRulesReducer.selectTemplateData);
  private templateDataChangedSubscription$: Subscription;
  private ordersLoadingSubscription$: Subscription;
  private freeSearchSubscription$: Subscription;
  private sortFields: SortField[] | 'NONE';
  private tableFilters: Filter[];
  private wrappedFilters: WrapFilter[] = [];
  private setOrdersCounterSubscription$: Subscription;
  private queryParamsSubscription$: Subscription;
  private orderIdSearchParams: string = null;
  private readonly emitTimeoutMS = 10000;
  private shopMetadata: IShopMetadata | any;

  constructor(
    public dialog: MatDialog,
    public apiService: ApiService,
    private orderService: OrderService,
    private userService: UserService,
    public utilService: UtilService,
    private router: Router,
    private cookieService: CookieService,
    private store: Store,
    private wsSubscriber: WebsocketSubscriberService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private spService: SidePanelService,
    private shopService: ShopService,
    private tableService: TableService,
    private _printService: PrintService,
    private shortCutService: KeyShortCutService,
    private boxService: BoxService,
  ) {
    this.ordersPerPage = this.orderService.getOrderPerPage();
    this.queryParamsOrderJump();
  }

  ngOnInit(): void {
    this.initGroupActions();
    this.initShopService();
    this.initShortcuts();
    this.loadLocalStorageDefaultColumns();
    this.initOrderCounters();
    this.setOrderCounters();
    this.onLoadOrders();
    this.queryParamsOrderJump();
    this.initSidePanelService();
    this.initTableService();
    this.initTemplateDataSubscription();
    this.initOrdersInProcess();
  }

  initOrdersInProcess() {
    this.wsSubscriber
      .fireOrdersInProcess()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res: OrdersInProcess) => {
        if (res.orders?.length) {
          res.orders.forEach((order: any) => {
            this.tableService.ordersInProcessMap.set(order.id, order.inProcess);
          });
          this.refreshOrdersState(res.orders as any);
          this.refreshCurrentPage();
        }
      });
  }

  initTableService() {
    this.tableService.onRefreshTableOrders$.pipe(takeUntil(this.destroyed$)).subscribe((orders) => {
      this.refreshOrdersState(orders);
    });

    this.tableService.popoverPressedRowId$.pipe(takeUntil(this.destroyed$)).subscribe((rowId) => {
      this.focusedOrderId = rowId;
    });
  }

  initGroupActions() {
    const currentUserRole: any = this.userService.currentMeUser?.roles;
    this.groupActions = [GroupAction.DELETE_ORDERS, GroupAction.MANUAL_VERIFY, GroupAction.EXPORT_ORDERS];
    if (!currentUserRole?.includes('WRITE_NO_TAGS')) {
      this.groupActions.push(GroupAction.TAG_ORDERS);
    }
    this.groupActions.push(
      GroupAction.TEAM_NOTE,
      GroupAction.RE_SYNC_ORDERS,
      GroupAction.COLOR_CODING,
      GroupAction.SHIPMENT_PREFERENCES,
      GroupAction.SHIPMENT_EXTRAS,
    );
  }

  initShopService(): void {
    this.shopService.shop$.pipe(takeUntil(this.destroyed$)).subscribe((shop) => {
      if (!shop) {
        return;
      }

      if (this.useCursorPagination()) {
        this.deliverCursorPagination.fetchTabCounters();
      }

      this.getPagedOrders(0, this.ordersPerPage);
      this.shop = shop;
      this.shopMetadata = shop.metadata;
      this.shopLocations = shop?.shopLocations;
      this.isCheckoutOnlyShop = shop?.isCheckoutOnly;
      this.disableQuickflows = !this.userService.isAdmin() && this.isCheckoutOnlyShop;

      const additionalGroupActions = getAdditionalGroupActionsByShop(shop);
      const additionalRowActions = getAdditionalRowActionsByShop(shop);

      this.groupActions = Array.from(new Set([...this.groupActions, ...additionalGroupActions]));
      this.rowActions = Array.from(new Set([...this.rowActions, ...additionalRowActions]));
      this.adjustColumns(shop);
    });
    this.shopService.shopDefaultBoxes$.pipe(takeUntil(this.destroyed$)).subscribe(({ activeBoxes, inactiveBoxes }) => {
      this.shopBoxes = [...activeBoxes];

      this.keyboardShortcuts = this.boxService.getBoxKeyboardShortcuts(this.shopBoxes);
    });
  }

  initShortcuts() {
    this.shortCutService
      .onShortcut$(this.shortcutScope)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((shortcut) => {
        this.editPackageOrders({ shortcut });
      });
  }

  adjustColumns(shop: Shop) {
    this.columns = initColumnsOptionsByShop(
      this.columns,
      shop,
      [ColumnName.INVENTORY_LOCATION],
      this.apiService.userLocationIds,
    );
    this.addNotVerifiedFilter(shop.metadata.notVerifiedName ?? ValidationStatuses.NOT_VERIFIED);
  }

  initSidePanelService(): void {
    this.spService.focusedOrderId$.pipe(takeUntil(this.destroyed$)).subscribe(({ focusedId }) => {
      this.focusedOrderId = focusedId ?? null;
    });
  }

  queryParamsOrderJump(): void {
    this.queryParamsSubscription$ = this.route.queryParams
      .pipe(takeUntil(this.destroyed$))
      .subscribe((queryParam: any) => {
        this.orderIdSearchParams = queryParam.search;
        if (this.orderIdSearchParams) {
          this.filter = FilterTypes.QUERY_PARAMS;
          this.getPagedOrders(0, this.ordersPerPage);
        }
      });
  }

  initOrderCounters(): void {
    if (this.useCursorPagination()) return;

    this.orderCounterSubscription$ = this.orderService
      .getPreviewOrderCounters()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((counters) => {
        this.countAllOrders = counters.all;
        this.countFixOrders = counters.fix;
        this.countValidatedOrders = counters.verified;
      });
  }

  // TODO filter per required column
  orderMatchTableFilterView(order: Order) {
    let orderInTableFilter = true;
    for (let i = 0; i < this.tableFilters?.length; i++) {
      if (
        (this.tableFilters[i].values.some((val) => val?.includes('fix')) &&
          this.tableFilters[i].fieldName === 'orderStatus' &&
          this.tableFilters[i].operator === 'IN') ||
        this.tableFilters[i].fieldName === 'missingPhone' ||
        this.tableFilters[i].fieldName === 'missingAddress'
      ) {
        const orderStatus: any = order.orderStatus;
        orderInTableFilter = orderStatus?.includes('fix');
        if (!orderInTableFilter) {
          return false;
        }
      }
    }
    return orderInTableFilter;
  }

  notInView(order: Order) {
    return (
      order.deleted ||
      !order.orderStatus?.includes(OrderStatus.STEP_NEW) ||
      !this.orderMatchesView(order) ||
      !this.orderMatchTableFilterView(order)
    );
  }

  refreshOrdersState(orders: Order[]) {
    const inc = 0;
    orders.forEach((order: Order) => {
      (order as any).inProcess = this.tableService.ordersInProcessMap.get(order.id) ?? false;
      this.tableService.deleteOrderCachedMap(order.id);
      let found = false;
      for (let i = 0; i < this.rows.length; i++) {
        if (this.rows[i].id === order.id) {
          found = true;
          let isPrioritize;
          if (
            !this.rows[i].orderOperation?.includes(OrderOperation.PRIORITIZE) &&
            order.orderOperation?.includes(OrderOperation.PRIORITIZE)
          ) {
            isPrioritize = true;
          } else {
            this.rows[i] = { ...this.rows[i], ...order };
          }
          if (this.notInView(this.rows[i])) {
            this.rows.splice(i, 1);
          }

          if (this.rows[i] && this.rows[i].splitIndices?.length !== order.splitIndices?.length) {
            this.rows = [...this.rows];
          }
          if (isPrioritize) {
            order = { ...this.rows[i], ...order };
            this.rows.splice(i, 1);
            this.rows = [order, ...this.rows];
          }
        }
      }

      if (!found) {
        if (this.isFiltersOnActiveMode) {
          // if table filters active, dont push new order to table filter
          // we should create filter mechanism by column condition to filter the current filters with the new order
          return;
        }
        // new pushed order
        if (this.filter !== FilterTypes.FREE_SEARCH && !this.notInView(order)) {
          this.pushOrderStrategy(order);
        }
      }
    });

    this.store.dispatch(
      PreviewOrdersActions.loadPreviewOrdersSuccess({
        orders: this.rows,
        count: 0,
        inc,
      }),
    );
  }

  splitOrderPush(order) {
    const prevSplittedOrderIndex = this.rows.findIndex((row) => {
      const isSameOrderNumber = row.orderNumber === order.orderNumber;
      const isSameOldSplitIndices = row.splitIndex === order.splitIndex - 1;
      const isSameNewSplitIndices = isSameSplitIndex(row, order);

      return isSameOrderNumber && (isSameOldSplitIndices || isSameNewSplitIndices);
    });
    if (prevSplittedOrderIndex > -1) {
      this.rows.splice(prevSplittedOrderIndex + 1, 0, order);
    } else if (order.orderOperation?.includes(OrderOperation.PRIORITIZE)) {
      this.prioritizeOrderPush(order);
    } else {
      this.rows.push(order);
    }
  }

  prioritizeOrderPush(order) {
    const lastPrioritizeOrderIndex = this.rows.findLastIndex(
      (row) => row.orderOperation && row.orderOperation.includes(OrderOperation.PRIORITIZE),
    );
    if (lastPrioritizeOrderIndex > -1) {
      this.rows.splice(lastPrioritizeOrderIndex + 1, 0, order);
    } else {
      this.rows.push(order);
    }
  }

  pushOrderStrategy(order) {
    if (order.splitted) {
      this.splitOrderPush(order);
    } else if (order.orderOperation?.includes(OrderOperation.PRIORITIZE)) {
      this.prioritizeOrderPush(order);
    } else {
      this.rows.push(order);
    }
  }

  setOrderCounters(): void {
    this.setOrdersCounterSubscription$ = this.wsSubscriber
      .fireVerifyOrdersCounterChanged()
      .pipe(
        throttleTime(this.emitTimeoutMS, asyncScheduler, { leading: true, trailing: true }),
        takeUntil(this.destroyed$),
      )
      .subscribe((data: WSEventData) => {
        if (this.useCursorPagination()) {
          const hasUserLocations = !!this.apiService.userLocationIds?.length;
          const countRelevantToCurrentUser =
            !hasUserLocations || _.isEqual(this.apiService.userLocationIds, data.userLocationCounters?.locationIds);
          this.handleCursorCounterUpdate(data);
          if (data.orders && countRelevantToCurrentUser) {
            this.refreshOrdersState(data.orders);
          } else {
            this.pullOrdersByCursor('current', this.sortFields);
          }
          return;
        }

        if (this.apiService.userLocationIds?.length) {
          const isCountAdjustToUser = _.isEqual(
            this.apiService.userLocationIds,
            data.userLocationCounters?.locationIds,
          );
          if (isCountAdjustToUser) {
            const counters = isCountAdjustToUser && data.userLocationCounters;
            this.countFixOrders = counters.fixCount;
            this.countValidatedOrders = counters.validatedCount;
            this.countAllOrders = counters.allCount;
            if (data.orders) {
              this.getPagedOrdersCount(this.activePage - 1, this.ordersPerPage, this.sortFields);
              this.refreshOrdersState(data.orders);
            } else {
              this.getPagedOrders(this.activePage - 1, this.ordersPerPage, this.sortFields);
            }
          } else {
            this.orderService.getAllCounters(OrderCounterTypes.VALIDATE_PAGE).then((data) => {
              if (!data.userLocationCounters) return;
              const counters = data.userLocationCounters;
              this.countFixOrders = counters.fixCount;
              this.countValidatedOrders = counters.validatedCount;
              this.countAllOrders = counters.allCount;
              this.getPagedOrders(this.activePage - 1, this.ordersPerPage, this.sortFields);
            });
          }
        } else {
          this.countFixOrders = data.fixCount;
          this.countValidatedOrders = data.validatedCount;
          this.countAllOrders = data.allCount;
          if (data.orders) {
            this.getPagedOrdersCount(this.activePage - 1, this.ordersPerPage, this.sortFields);
            this.refreshOrdersState(data.orders);
          } else {
            this.getPagedOrders(this.activePage - 1, this.ordersPerPage, this.sortFields);
          }
        }
      });
  }

  onLoadOrders(): void {
    this.ordersSubscription$ = this.orders$.pipe(takeUntil(this.destroyed$)).subscribe((orders: Order[]) => {
      if (!orders || !orders.length) {
        orders = [];
      }

      this.rows = this.processOrders(orders);
      this.temp = this.rows;
      this.loading = false;
    });
    this.totalCountOrdersSubscription$ = this.totalCountOrders$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((totalCountOrders: number) => {
        this.totalCountOrders = totalCountOrders;
        this.totalPages = Math.ceil(totalCountOrders / this.ordersPerPage);
        this.utilService.setPages(this.activePage, this.allPages, this.totalPages);
      });
    this.ordersLoadingSubscription$ = this.ordersLoading$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((ordersLoading: boolean) => {
        // this.loading = ordersLoading;
        if (!this.isLoadedInitialData && !ordersLoading) {
          this.isLoadedInitialData = true;
        }
      });
  }

  onSearchToTable(event: { filter: string; searchTerm: string | number; searchField: string }): void {
    this.freeSearchValue = event.searchTerm;
    this.filter = event.filter;
    this.freeSearchSelectedField = event.searchField;
    this.activePage = 1;
    this.getPagedOrders(0, this.ordersPerPage);
  }

  loadDefaultPackagesOptions(index: number): void {
    this.shopService.shopDefaultBoxes$.pipe(takeUntil(this.destroyed$)).subscribe(({ activeBoxes, inactiveBoxes }) => {
      if (!activeBoxes) {
        return;
      }

      const boxOptions = [];
      let customBoxOpt;
      activeBoxes.forEach((box) => {
        const res = {
          value: box.name.toLowerCase(),
          text: box.name,
        };
        if (box.isCustom) {
          customBoxOpt = res;
        } else {
          boxOptions.push(res);
        }
      });
      if (customBoxOpt) boxOptions.push(customBoxOpt);
      this.columns[index].filterSelectOptions = boxOptions;
    });
  }

  loadLocalStorageDefaultColumns(): void {
    const data = localStorage.getItem('validate-columns');
    this.selectedColumns = data
      ? JSON.parse(data)
      : this.COLUMN_OPTIONS.filter((column: ColumnSettings) => !column.canBeHide || column.default).map(
          (column: ColumnSettings) => column.prop,
        );
    this.loadSelectedColumns();
  }

  loadSelectedColumns(): void {
    localStorage.setItem('validate-columns', JSON.stringify(this.selectedColumns));
    this.columns = previewColumnsConfig.filter(
      (column: ColumnSettings) => !column.canBeHide || this.selectedColumns.includes(column.prop),
    );
    const packageColumnIndex = this.columns.findIndex((col) => col.name === 'Package name');
    if (packageColumnIndex > 0) {
      this.loadDefaultPackagesOptions(packageColumnIndex);
    }
  }

  toggleColumn(): void {
    this.loadSelectedColumns();
  }
  private createNotVerifiedWrapFilter(): WrapFilter {
    const fixStatusFilter = new Filter('orderStatus', 'IN', Object.values(OrderFixStatuses));
    const giftCardFilter = new Filter('giftCard', 'IN', ['1']);
    const paymentStatusFilter = new Filter('paymentStatus', 'IN', [
      TranslatedPaymentStatus.CANCELLED,
      TranslatedPaymentStatus.FAILED,
    ]);
    return new WrapFilter([fixStatusFilter, giftCardFilter, paymentStatusFilter], 'OR');
  }
  private createVerifiedFilters(): Filter[] {
    return [
      new Filter('orderStatus', 'NOT IN', Object.values(OrderFixStatuses)),
      new Filter('giftCard', 'IN', ['0']),
      new Filter('paymentStatus', 'NOT IN', [TranslatedPaymentStatus.CANCELLED, TranslatedPaymentStatus.FAILED]),
    ];
  }
  buildQueryParameters(page, maxsize, sorting: SortField[] | 'NONE' = null): QueryParameters {
    const queryParameters = sorting ? this.createQueryParameters(page, sorting) : this.createQueryParameters(page);
    if (this.tableFilters && this.tableFilters.length) {
      queryParameters.filters = queryParameters.filters || [];
      queryParameters.filters = [...queryParameters.filters, ...this.tableFilters];
      queryParameters.wrapFilters = queryParameters.wrapFilters || [];
    }
    if (this.wrappedFilters?.length) {
      queryParameters.wrapFilters = [...queryParameters.wrapFilters, ...this.wrappedFilters];
    }
    switch (this.filter) {
      case FilterTypes.QUERY_PARAMS: {
        queryParameters.filters.push(new Filter('order.id', 'EQUAL', [this.orderIdSearchParams]));
        break;
      }
      case FilterTypes.VERIFIED: {
        queryParameters.filters = queryParameters.filters.concat(this.createVerifiedFilters());
        break;
      }
      case FilterTypes.FIX: {
        queryParameters.wrapFilters.push(this.createNotVerifiedWrapFilter());
        break;
      }
      case FilterTypes.FREE_SEARCH: {
        queryParameters.filters = queryParameters.filters || [];
        const filter = this.getFreeSearchFilter();
        queryParameters.filters.push(filter);
        break;
      }
      case FilterTypes.FREE_TEXT: {
        queryParameters.search = this.freeSearchValue;
        break;
      }
      case FilterTypes.MULTIPLE: {
        queryParameters.filters = queryParameters.filters || [];
        const filter = this.getMultipleFilter();
        queryParameters.filters.push(filter);
      }
    }
    return queryParameters;
  }

  orderMatchesView(order: Order) {
    const filters = this.buildQueryParameters(0, 0);
    switch (this.filter) {
      case FilterTypes.QUERY_PARAMS: {
        break;
      }
      case FilterTypes.VERIFIED: {
        const orderStatusFilter = filters.filters.find((filter) => filter.fieldName == 'orderStatus');
        const paymentStatusFilter = filters.filters.find((filter) => filter.fieldName == 'paymentStatus');

        const isInOrderStatusFilter = orderStatusFilter.values.find((val) =>
          order.orderStatus?.includes(val as OrderStatus),
        );
        if (isInOrderStatusFilter) {
          return false;
        }

        const isInPaymentStatusFilter = paymentStatusFilter.values.find((val) =>
          order.paymentStatus?.includes(val as TranslatedPaymentStatus),
        );
        if (isInPaymentStatusFilter) {
          return false;
        }

        return true;
      }

      case FilterTypes.FIX: {
        // fix type, only includes wrap filters
        let orderStatusFilter;
        let paymentStatusFilter;

        filters.wrapFilters.forEach((wrapFilter) => {
          orderStatusFilter = wrapFilter.filters.find((filter) => filter.fieldName === 'orderStatus');
          paymentStatusFilter = wrapFilter.filters.find((filter) => filter.fieldName === 'paymentStatus');
        });
        const isInOrderStatusFilter = orderStatusFilter.values?.find((val) =>
          order.orderStatus?.includes(val as OrderStatus),
        );
        if (isInOrderStatusFilter) {
          return true;
        }

        const isInPaymentStatusFilter = paymentStatusFilter.values?.find((val) =>
          order.paymentStatus?.includes(val as TranslatedPaymentStatus),
        );
        if (isInPaymentStatusFilter) {
          return true;
        }

        // not matching the view
        return false;
      }

      case FilterTypes.FREE_SEARCH: {
        break;
      }

      case FilterTypes.FREE_TEXT: {
        break;
      }

      case FilterTypes.MULTIPLE: {
      }
    }
    return true;
  }

  getPagedOrders(page, maxsize, sorting: SortField[] | 'NONE' = null): void {
    if (this.useCursorPagination()) {
      this.pullOrdersByCursor(page === 0 ? 'first' : 'current', sorting);
      return;
    }

    this.loading = true;
    const queryParameters = this.buildQueryParameters(page, maxsize, sorting);
    this.store.dispatch(PreviewOrdersActions.loadPreviewOrders({ queryParameters }));
  }

  getPagedOrdersCount(page, maxsize, sorting: SortField[] | 'NONE' = null): void {
    const queryParameters = this.buildQueryParameters(page, maxsize, sorting);
    this.store.dispatch(PreviewOrdersActions.loadPreviewOrdersCount({ queryParameters }));
  }

  private refreshCurrentPage(): void {
    if (this.useCursorPagination()) {
      this.pullOrdersByCursor('current', this.sortFields);
      return;
    }

    this.getPagedOrders(this.activePage - 1, this.ordersPerPage, this.sortFields);
  }

  private pullOrdersByCursor(action: PaginationCursorAction = 'first', sorting: SortField[] | 'NONE' = null): void {
    this.loading = true;
    this.store.dispatch(PreviewOrdersActions.setPreviewOrdersLoading({ loading: true }));
    const query = this.buildQueryParameters(0, this.ordersPerPage, sorting);

    this.deliverCursorPagination
      .fetchOrders(action, query, this.ordersPerPage)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe();

    if (action === 'first' || action === 'current') {
      this.deliverCursorPagination.fetchTotalCount(query);
    }
  }

  onCursorPageChange(action: PaginationCursorAction): void {
    this.pullOrdersByCursor(action, this.sortFields);
  }

  private getCappedCounterLabel(label: string, counterKey?: keyof DeliverCursorPagination.VerifyTabCounters): string {
    const tabCounters = this.deliverCursorPagination.tabCounters();
    if (!tabCounters) return label;

    if (!counterKey) {
      const keys = ['fix', 'validated'] as const;
      const anyCapped = keys.some((key) => tabCounters[key].isCapped);
      if (anyCapped) {
        const maxCapped = Math.max(
          ...keys.filter((key) => tabCounters[key].isCapped).map((key) => tabCounters[key].count),
        );
        return `${label} (${maxCapped}+)`;
      }
      const total = keys.reduce((sum, key) => sum + tabCounters[key].count, 0);
      return `${label} (${total})`;
    }

    return `${label} (${this.deliverCursorPagination.formatCappedCount(tabCounters[counterKey])})`;
  }

  private handleCursorCounterUpdate(data: WSEventData): void {
    const hasUserLocations = !!this.apiService.userLocationIds?.length;
    const countRelevantToCurrentUser =
      !hasUserLocations || _.isEqual(this.apiService.userLocationIds, data.userLocationCounters?.locationIds);

    if (!countRelevantToCurrentUser) {
      this.deliverCursorPagination.fetchTabCounters();
      return;
    }

    const toCapped = (value: number) => this.deliverCursorPagination.toCappedCount(value, TABLES.VERIFY);
    const counters = hasUserLocations ? data.userLocationCounters : data;

    this.deliverCursorPagination.updateTabCounters({
      fix: toCapped(counters.fixCount),
      validated: toCapped(counters.validatedCount),
    });
  }

  getFreeSearchFilter(): Filter {
    return new Filter(this.freeSearchSelectedField, 'IN', [this.freeSearchValue]);
  }

  getMultipleFilter(): Filter {
    return new Filter(this.freeSearchSelectedField, 'IN', this.freeSearchValue);
  }

  clearFreeSearchFilter(): void {
    this.showFreeSearchSuggestions = false;
    this.searchToggleButtons = false;
    this.freeSearchValue = '';
    this.filter = FilterTypes.READY;
    this.activePage = 1;
    this.getPagedOrders(0, this.ordersPerPage);
  }

  showFreeSearchResults(value: string): void {
    this.showFreeSearchSuggestions = false;
    this.filter = FilterTypes.FREE_SEARCH;
    this.freeSearchValue = value;
    this.activePage = 1;
    this.getPagedOrders(0, this.ordersPerPage);
  }

  // Calls a freetext query on the selected field from the searchbox (as opposed to clicking a suggestion - which calls a filter query)
  showFreeTextSearchResults(): void {
    this.showFreeSearchSuggestions = false;
    this.filter = FilterTypes.FREE_TEXT;
    this.activePage = 1;
    this.getPagedOrders(0, this.ordersPerPage);
  }

  changeFreeSearchField(event): void {
    this.freeSearchInputControl.setValue(this.freeSearchValue);
  }

  getFreeSearchSuggestions(event): Observable<any[]> {
    if (event.length === 0) {
      this.showFreeSearchClearButton = false;
      this.searchToggleButtons = false;
      this.showFreeSearchSuggestions = false;
      return EMPTY;
    }
    this.showFreeSearchClearButton = true;
    this.showFreeSearchSuggestions = true;
    this.searchToggleButtons = true;
    return this.orderService.getSearchOrders(
      new FreeSearchParameters(
        this.freeSearchValue,
        [this.freeSearchSelectedField],
        createStepFilter(OrderStatus.STEP_NEW),
      ),
    ) as Observable<any[]>;
  }

  ngAfterViewInit(): void {
    // freesearch server-side autocomplete
    if (this.freeSearchSubscription$) {
      this.freeSearchSubscription$.unsubscribe();
    }
    this.freeSearchSubscription$ = this.freeSearchInputControl.valueChanges
      .pipe(
        debounceTime(300),
        switchMap((value) => this.getFreeSearchSuggestions(value)),
        takeUntil(this.destroyed$),
      )
      .subscribe((data) => {
        this.searchResults = data;
        this.showFreeSearchSuggestions = this.searchResults.length > 0;
      });
  }

  sortColumns(sortOptions): void {
    this.columns = this.columns.map((column: ColumnSettings, i: number) => {
      if (i !== sortOptions.index) {
        return { ...column, sortDirection: null };
      }

      const sortDirection = sortOptions.sortDirection;
      const sortFieldNames: string[] = column.sortFieldName ? column.sortFieldName : [column.prop];

      this.sortFields = sortFieldNames.map((fieldName: string) => new SortField(fieldName, sortDirection));

      return {
        ...column,
        sortDirection,
      };
    });

    this.getPagedOrders(this.activePage - 1, this.ordersPerPage, this.sortFields);
  }

  onChangeTableFilter(columnIndex: number): void {
    this.loading = true;
    this.tableFilters = [];
    this.wrappedFilters = [];
    this.sortFields = undefined;
    let fieldName;
    this.columns.forEach((column: ColumnSettings) => {
      if (!column.filterType || !column.filterOption || (!column.filterValue && !column.filterValueArray?.length)) {
        return;
      }
      if (column.prop === 'orderStatus' && column.filterValue === 'verified') {
        this.tableFilters = this.tableFilters.concat(this.createVerifiedFilters());
        return;
      }
      if (column.type === ColumnTypes.VALIDATED_STATUS) {
        if (column.filterValue === 'pickUpLocationId') {
          this.tableFilters.push(new Filter('orderStatus', 'NOT IN', Object.values(OrderFixStatuses)));
          this.tableFilters.push(new Filter('pickUpLocationId', 'NOT IN', ['0']));
          return;
        }
        if (column.filterValue === DeliveryPromiseLabel.POS) {
          this.tableFilters.push(new Filter('tableOrderTag', 'IN', ['pos']));
          return;
        }
        if (column.filterValue === OrderStatus.FIX_PENDING) {
          this.tableFilters.push(new Filter('orderStatus', 'IN', [OrderStatus.FIX_PENDING]));
          return;
        }
        if (column.filterValue === OrderStatus.FIX_INVENTORY_OUT_OF_STOCK) {
          this.tableFilters.push(new Filter('orderStatus', 'IN', [OrderStatus.FIX_INVENTORY_OUT_OF_STOCK]));
          return;
        }

        if (column.filterValue === OrderStatus.FIX_WEATHER) {
          this.tableFilters.push(new Filter('orderStatus', 'IN', [OrderStatus.FIX_WEATHER]));
          return;
        }

        if (column.filterValue === OrderStatus.FIX_SPLIT) {
          this.tableFilters.push(new Filter('orderStatus', 'IN', [OrderStatus.FIX_SPLIT]));
          return;
        }

        if (column.filterValue === FlaggedStatuses.FLAG_NOT_VERIFIED) {
          this.tableFilters.push(new Filter('orderStatus', 'IN', [OrderStatus.FIX_NOT_VERIFIED]));
          return;
        }

        if (column.filterValue === OrderStatus.FIX_NOT_VERIFIED) {
          this.wrappedFilters.push(this.createNotVerifiedWrapFilter());
          return;
        }

        if (column.filterValue === TranslatedPaymentStatus.CANCELLED) {
          this.tableFilters.push(new Filter('paymentStatus', 'IN', [TranslatedPaymentStatus.CANCELLED]));
          return;
        }
        if (column.filterValue === 'giftCard') {
          this.tableFilters.push(new Filter('giftCard', 'IN', ['1']));
          return;
        }
        if (column.filterValue === TranslatedPaymentStatus.REFUNDED) {
          this.tableFilters.push(new Filter('paymentStatus', 'EQUAL', [TranslatedPaymentStatus.REFUNDED]));
          return;
        }
        if (column.filterValue === TranslatedPaymentStatus.PARTIALLY_REFUNDED) {
          this.tableFilters.push(new Filter('paymentStatus', 'LIKE', [TranslatedPaymentStatus.PARTIALLY_REFUNDED]));
          return;
        }
        if (column.filterValue === FlaggedStatuses.MISSING_PHONE) {
          this.tableFilters.push(new Filter('missingPhone', 'LIKE', []));
          return;
        }
        if (column.filterValue === FlaggedStatuses.MISSING_ADDRESS) {
          this.tableFilters.push(new Filter('missingAddress', 'LIKE', []));
          return;
        }
        if (column.filterValue === TranslatedPaymentStatus.FAILED) {
          this.tableFilters.push(new Filter('paymentStatus', 'IN', [TranslatedPaymentStatus.FAILED]));
          return;
        }
        if (column.filterValue === OrderStatus.CANCELED) {
          this.tableFilters.push(new Filter('orderStatus', 'IN', [OrderStatus.CANCELED]));
          return;
        }
        if (column.filterValue === OrderStatus.FIX_ON_HOLD) {
          this.tableFilters.push(new Filter('orderStatus', 'IN', [OrderStatus.FIX_ON_HOLD]));
          return;
        }
      }

      fieldName = column.filterFields ? column.filterFields : column.prop;
      let filterValue: string[];

      filterValue = [column.filterValue];

      if (column.filterType === TableFilterType.COLOR) {
        filterValue = column.filterValueArray;
      } else if (column.filterOption === 'DATE RANGE' || column.filterOption === 'DATE RANGE STRICT') {
        filterValue = [];
        if (column.filterValueStart) {
          filterValue.push(column.filterValueStart);
        }
        if (column.filterValueEnd) {
          const endDate = new Date(Date.parse(column.filterValueEnd));
          endDate.setDate(endDate.getDate() + 1);
          modifyDateRangeValuesByColumn(column.prop, { endDate: endDate });
          filterValue.push(endDate.toISOString());
        }
      } else if (column.filterOption === 'LIKE' && !this.shopMetadata?.applySortingOnFilters) {
        // if we have to add a sort field in the future, consider use id instead of createdDate for better performance
        this.sortFields = 'NONE';
      }

      if (column.filterType !== TableFilterType.TAGS && column.type !== ColumnTypes.PACKAGES) {
        this.tableFilters.push(
          new Filter(
            fieldName,
            column.filterOption,
            filterValue.map((x) => (x?.trim ? x.trim() : x)),
          ),
        );
      }

      if (column.filterType === TableFilterType.TAGS && column.filterValueArray?.length > 0) {
        column.filterValueArray.forEach((filterValue) => {
          this.tableFilters.push(new Filter(filterValue.type, column.filterOption, [filterValue.name]));
        });
      }

      if (column.type === ColumnTypes.PACKAGES) {
        const options: any = {};
        if (this.shopMetadata?.isMultiParcelSupport) {
          options.isMultiParcel = true;
        }
        this.tableFilters.push(new Filter(fieldName, column.filterOption, filterValue, options));
      }
    });

    this.filterActive = fieldName ? true : false;
    this.isFiltersOnActiveMode = this.tableFilters?.length > 0;
    this.activePage = 1;
    this.getPagedOrders(this.activePage - 1, this.ordersPerPage, this.sortFields);
  }

  onChangeSelectedOrders(selectedOrders: Order[]): void {
    this.selectedValidOrders = [];
    this.selectedOrders = selectedOrders;
    this.selectedOrders.map((selectedOrder) => {
      if (this.checkValidationStatus(selectedOrder)) {
        this.selectedValidOrders.push(selectedOrder);
      }
    });

    if (this.selectedOrders.length > 0) {
      this.shortCutService.setScope(this.shortcutScope);
    } else {
      this.shortCutService.revertScope(this.shortcutScope);
    }
  }

  processOrders(rows: any): Order[] {
    return rows.map((row) => {
      row = { ...row };
      if (row.customer) {
        row.customerFormated = formatBillTo(row, this.shopMetadata?.isHideEmailInBillTo);
      }
      if (this.shopMetadata?.isMultiParcelSupport) {
        row.box = {};
        row.box.name = formatMultiParcelBoxName(row.itemCount);
      }
      row.deliveryPromise = getDeliveryPromise(row);
      row.inProcess = this.tableService.ordersInProcessMap.get(row.id) ?? false;
      row.formattedShippingCityAndProvince = formatShippingAddressString(row);
      row.foramttedShippingName = formatShippingNameString(row);
      if (!row.foramttedShippingName && !row.foramttedShippingName) {
        row.foramttedShippingName = 'Missing details';
        row.missingShippingDetails = true;
      }
      row.currentColorTooltipString =
        !row.colorCoding || row.colorCoding.includes('transparent')
          ? ''
          : row.colorCoding
              .map((colorValue) => {
                const current = this.shop.colorCoding?.find((colorData) => colorData.value === colorValue);
                return current?.label || current?.defaultName || '';
              })
              .join(', ');
      return row;
    });
  }

  createQueryParameters(
    page: number,
    sortFields: SortField[] | 'NONE' = [new SortField(getDefaultSortField(this.shopService.shopPlatform), 'ASC')],
  ): QueryParameters {
    const queryParameters = new QueryParameters();
    queryParameters.filters = [new Filter('deleted', 'IN', ['0'])];
    queryParameters.page = page;
    queryParameters.maxResults = this.ordersPerPage;
    queryParameters.anyAll = 'all';
    queryParameters.stepFilter = createStepFilter(OrderStatus.STEP_NEW);
    queryParameters.sortFields = sortFields == 'NONE' ? undefined : sortFields;
    queryParameters.wrapFilters = [];
    return queryParameters;
  }

  toSelectedOrderActions(object: any): void {
    object?.row ? (this.selectedOrders = [object?.row]) : this.selectedOrders;
    const state: string = object?.event;
    const preText: string = object?.preText;
    const postText: string = this.selectedOrders?.length < 2 ? object?.postText : object?.multiplePostText;
    const dontDisplayOrderNumbers: boolean = object?.dontDisplayOrderNumbers;
    const dialogRef = this.dialog.open(HolddialogComponent, {
      maxWidth: '100vw',
      width: '28vw',
      panelClass: 'hold_popup',
      backdropClass: 'global_modal_overlay',
      disableClose: true,
      data: {
        rows: this.selectedOrders,
        state,
        preText,
        postText,
        dontDisplayOrderNumbers,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((snackbar) => {
        if (snackbar) {
          this.showSnackbar(snackbar.message, snackbar.mode);
        }
        this.selectedOrders = [];
      });
  }

  onBulkPrint(action): void {
    const fromAssign = true;
    const ids = this.selectedOrders.map((order) => order.id);
    this._printService.printOrder(action, ids, fromAssign);
  }

  onDeleteOrder(row): void {
    this.dialog.open(DeleteOrderDialogComponent, {
      width: '612px',
      disableClose: true,
      data: {
        rows: [row],
        table: TABLES.VERIFY,
      },
    });
  }

  sendOrdersToAssign(): void {
    this.loading = true;
    this.assignLoader = !this.assignLoader;
    setTimeout(() => {
      this.loading = false;
    }, 2500);
    let ordersToAssign: string[] = [];
    if (this.selectedValidOrders.length > 0) {
      this.rows = [...this.selectedValidOrders];
    }

    this.rows.forEach((row) => {
      if (this.checkValidationStatus(row)) {
        ordersToAssign.push(row.id);
      }
    });
    if (this.selectedValidOrders.length < 1) {
      ordersToAssign = [];
    }
    this.orderService.sendSelectedToAssign(ordersToAssign).then(() => {
      this.router.navigate(['/deliver/manage/assign']);
    });
  }

  reSyncOrderClick(row, fromSidePanel?: boolean): void {
    this.isSyncingOrders = true;
    this.setRowIsSyncing(row, true, fromSidePanel);

    this.orderService
      .syncOrder(row.id)
      .then(() => {
        this.toastService.show(PdqToastComponent, {
          message: `Order #${row.orderNumber} synced`,
          mode: 'info',
          duration: TimeUnits._SECOND_IN_MS * 3,
        });
      })
      .catch(() => {})
      .finally(() => {
        this.setRowIsSyncing(row, false, fromSidePanel);
        this.isSyncingOrders = false;
      });
  }
  setRowIsSyncing(row, newIsSyncing: boolean, fromSidePanel?: boolean) {
    this.rows.some((listedRow, index) => {
      if (listedRow.id === row.id) {
        this.rows[index].isSyncing = newIsSyncing;
        row = { ...row, isSyncing: newIsSyncing };
        return true;
      }
      return false;
    });
    if (fromSidePanel) {
      this.spService.setActiveOrder(row);
    }
    this.temp = [...this.rows];
  }

  reSyncSelectedOrder(): void {
    this.isSyncingOrders = true;
    const ordersID = this.selectedOrders.map((order: Order) => order.id);
    this.orderService
      .syncOrders(ordersID)
      .then((res) => {
        if (res?.unSupported) {
          this.toastService.showToast({
            message: res.unSupported,
            mode: 'danger',
          });
        } else {
          this.toastService.show(PdqToastComponent, {
            message: `${this.selectedOrders?.length} order${this.selectedOrders?.length > 1 ? 's' : ''} synced`,
            mode: 'info',
            duration: TimeUnits._SECOND_IN_MS * 3,
          });
        }
      })
      .catch(() => {
        this.toastService.showToast({
          message: `Unable to sync those orders`,
          mode: 'danger',
        });
      })
      .finally(() => {
        this.selectedOrders = [];
        this.isSyncingOrders = false;
      });
  }

  changeColorCoding({ order, colors, selectedOrders }) {
    const orderIds: string[] = selectedOrders ? this.selectedOrders.map((order) => order.id) : [order.id];
    const previousRows = this.rows;
    this.assignColorsCodingUpdate(orderIds.map((id) => ({ id, colorCoding: colors })));
    this.orderService.updateOrdersColorCoding(orderIds, colors).subscribe({
      next: (succededOrders) => {
        if (succededOrders && succededOrders?.length < orderIds.length) {
          const rowsToRevert = previousRows.filter(
            (row) => !succededOrders.some((succededOrder) => succededOrder.id === row.id),
          );
          this.assignColorsCodingUpdate(rowsToRevert);
        }
      },
      error: (error) => {
        this.assignColorsCodingUpdate(previousRows);
        this.toastService.showToast({
          message: `Failed to update color coding`,
          mode: 'danger',
        });
      },
    });
  }

  bulkChangeColorCoding({ orderColors }: { orderColors: Pick<Order, 'id' | 'colorCoding'>[] }) {
    const previousRows = this.rows;
    this.assignColorsCodingUpdate(orderColors);
    this.orderService.updateOrdersColorCodingV2(orderColors).subscribe({
      error: (error) => {
        this.assignColorsCodingUpdate(previousRows);
        this.toastService.showToast({
          message: `Failed to update color coding`,
          mode: 'danger',
        });
      },
    });
  }

  private assignColorsCodingUpdate(updatedOrders: Pick<Order, 'id' | 'colorCoding'>[]) {
    const newRows = assignColorCodingToRows(this.rows, updatedOrders, this.shop.colorCoding);

    this.selectedOrders = [...newRows.filter((order) => this.selectedOrders.some((o) => o.id === order.id))];

    const spOrder = this.spService.getActiveOrder();
    if (spOrder?.id) {
      const spColorCoding = updatedOrders.find((order) => order.id === spOrder?.id)?.colorCoding;
      this.spService.setActiveOrder({ ...spOrder, colorCoding: spColorCoding });
    }
    this.temp = [...newRows];
    this.rows = [...newRows];
  }

  deleteOrder(row): void {
    this.orderService.deleteOrder([row.id]);
  }

  deleteSelectedOrder(): void {
    this.dialog
      .open(DeleteOrderDialogComponent, {
        width: '612px',
        disableClose: true,
        data: {
          rows: this.selectedOrders,
          table: TABLES.VERIFY,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.selectedOrders = [];
      });
  }

  paginate(page: number): void {
    this.activePage = page + 1;
    this.utilService.setPages(this.activePage, this.allPages, this.totalPages);
    this.getPagedOrders(page, this.ordersPerPage);
  }

  onTableActivate({ row }) {
    if (!row.id) {
      return;
    }

    this.spService.setFocusedOrderId(row.id);
  }

  jumpToPage(page: number): void {
    if (!page) {
      alert('Please enter a valid number');
      return;
    }

    if (page > this.totalPages || page < 1) {
      alert(`Please enter a Page no. less than ${this.totalPages}`);
      return;
    }

    this.activePage = page;
    this.utilService.setPages(this.activePage, this.allPages, this.totalPages);
    this.getPagedOrders(this.activePage - 1, this.ordersPerPage);
  }

  onChangeOrdersPerPage(ordersPerPage: number): void {
    this.ordersPerPage = ordersPerPage;
    this.cookieService.set('orderperpage_cookie', ordersPerPage.toString());
    this.activePage = 1;
    this.totalPages = Math.ceil(this.totalCountOrders / this.ordersPerPage);
    this.utilService.setPages(this.activePage, this.allPages, this.totalPages);
    this.getPagedOrders(0, this.ordersPerPage);
  }

  changeFilter(value): void {
    // on change filter reset the selected orders
    this.selectedOrders = [];
    this.selectedValidOrders = [];

    // reset search input
    this.searchAutocompleteRef.resetFormControl();

    this.filter = value;

    this.activePage = 1;
    this.getPagedOrders(0, this.ordersPerPage);
  }

  checkValidationStatus(row: Order): boolean {
    return !(
      row.giftCard ||
      row.orderStatus.some((status) => OrderFixStatuses.includes(status)) ||
      row.paymentStatus == TranslatedPaymentStatus.FAILED ||
      row.paymentStatus == TranslatedPaymentStatus.CANCELLED ||
      ![...PaymentValidStatuses, ...PaymentInvalidStatuses].some(
        (status) => status?.toLowerCase() == row.paymentStatus?.toLowerCase(),
      )
    );
  }

  chooseEmptyMessage() {
    return this.filterActive || this.filter == 'multiple'
      ? tableMessages.NO_RESULT_MESSAGE
      : tableMessages.EMPTY_MESSAGE;
  }

  ngOnDestroy(): void {
    this.input = null;
    this.searchAutocompleteRef = null;
    this.tableService.ordersInProcessMap?.clear();
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.freeSearchSubscription$?.unsubscribe();
    this.orderCounterSubscription$?.unsubscribe();
    this.ordersSubscription$?.unsubscribe();
    this.ordersLoadingSubscription$?.unsubscribe();
    this.totalCountOrdersSubscription$.unsubscribe();
    this.setOrdersCounterSubscription$?.unsubscribe();
    this.templateDataChangedSubscription$?.unsubscribe();
  }

  onBottomScrolled(onBottomScrolled: boolean): void {
    this.isBottomScrolled = onBottomScrolled;
  }

  getLabelAndCounterString(label: string, counter: number): string {
    return `${label} (${counter})`;
  }
  onManualVerifySelectedOrders(selectedOption: DropdownOption): void {
    if (!this.selectedOrders) {
      return;
    }

    // orders flagged as include fix address/phone
    const flaggedOrders = this.selectedOrders.filter((order) => {
      switch (selectedOption.type) {
        case 'orderStatus':
          const statusesToCheck = [];
          if (selectedOption.value == this.shopMetadata.notVerifiedName) {
            statusesToCheck.push(OrderStatus.FIX_NOT_VERIFIED);
          } else if (selectedOption.value == OrderStatus.FIX_PHONE) {
            statusesToCheck.push(OrderStatus.FIX_PHONE, OrderStatus.FIX_ADDRESS);
          } else {
            statusesToCheck.push(selectedOption.value);
          }
          return statusesToCheck.some((statusToCheck) => order.orderStatus.includes(statusToCheck));
        case 'flaggedStatus':
          if (selectedOption.value == FlaggedStatuses.MISSING_PHONE) {
            return !order.shippingPhone;
          }
          break;
        case 'dynamicStatus':
          return order.orderStatus.includes(OrderStatus.FIX_NOT_VERIFIED);
      }
    });

    const orderIds = flaggedOrders?.map((order) => order.id);

    if (orderIds?.length < 1) {
      this.showSnackbar(
        `There are no orders with the status '${selectedOption?.label}' among the selected orders.`,
        'danger',
      );
      return;
    }

    const dialogRef = this.dialog.open(ManualVerifyDialog, {
      panelClass: 'manual-verify-dialog',
      backdropClass: 'global_modal_overlay',
      data: { selectedOption },
    });

    dialogRef.componentInstance.onVerifyOrders.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.orderService.ordersIsLoading.next(true);

      this.orderService
        .manualVerifyOrders(
          orderIds,
          selectedOption as { value: string; type: 'orderStatus' | 'flaggedStatus' | 'dynamicStatus' },
        )
        .subscribe(
          (res) => {
            this.orderService.ordersIsLoading.next(false);
            dialogRef.close();
          },
          (err) => {
            this.showSnackbar(
              `Bulk approval of '${selectedOption?.label}' for selected orders has failed Failed`,
              'danger',
            );
            this.orderService.ordersIsLoading.next(false);
            dialogRef.close();
          },
        );
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.selectedOrders = [];
      });
  }

  splitOrder(row) {
    if (row.orderStatus.includes(OrderStatus.FIX_SPLIT)) {
      this.orderService.getOrder(row.id).then((orderData) => {
        this.dialog
          .open(ApproveSplitOrderComponent, {
            width: '612px',
            height: '300px',
            panelClass: 'unify-locations',
            data: orderData,
          })
          .afterClosed()
          .pipe(takeUntil(this.destroyed$))
          .subscribe((isSplit) => {
            if (isSplit) {
              this.rows = [...this.rows.filter((r) => r.id !== row.id)];
            } else if (isSplit === false) {
              this.showSnackbar('Failed to split order', 'danger');
            }
          });
      });
    } else {
      this.dialog
        .open(SplitOrderComponent, {
          width: '871px',
          height: '747px',
          panelClass: 'unify-locations',
          data: row,
        })
        .afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((isSplit) => {
          if (isSplit) {
            this.rows = [...this.rows.filter((r) => r.id !== row.id)];
          }
        });
    }
  }

  changeLocationSelectedOrders(newLocationId: number) {
    if (this.selectedOrders.length > 9) {
      this.dialog
        .open(ChangeLocationDialogComponent, {
          width: '612px',
          height: '208px',
          panelClass: 'unify-locations',
          data: { count: this.selectedOrders.length },
        })
        .afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((isChange) => {
          if (isChange) {
            this.changeLocation(newLocationId);
          }
        });
    } else {
      this.changeLocation(newLocationId);
    }
  }

  changeLocation(newLocationId: number) {
    this.isSyncingOrders = true;
    const ids = [];
    let countOrdersExistInLocation = 0;
    const shipFromAddress = this.shopLocations.find((l) => l.locationId === newLocationId);
    this.selectedOrders.forEach((row, index) => {
      if (row.shipFromAddress?.id === shipFromAddress.id) {
        countOrdersExistInLocation++;
      } else if (
        row.fulfillmentStatus === TranslatedFulfillmentStatus.FULFILLED ||
        row.fulfillmentStatus === TranslatedFulfillmentStatus.PARTIALLY_FULFILLED ||
        row.pickUpLocationId ||
        row.orderStatus.includes(OrderStatus.FIX_SPLIT)
      ) {
        const orderNumber = row.splitted
          ? `${row.orderNumber}${getOrderSplitIndicesString(row, 'view', true)}`
          : row.orderNumber;
        setTimeout(() => {
          this.showSnackbar(
            `Failed to change inventory location for`,
            'danger',
            {
              label: `${
                row.pickUpLocationId
                  ? 'pickup'
                  : row.orderStatus.includes(OrderStatus.FIX_SPLIT)
                    ? 'split required'
                    : 'fulfilled'
              } order #${orderNumber}`,
              variant: 'monochrome',
            },
            row.id,
          );
        }, index * 200);
      } else {
        this.setRowIsSyncing(row, true);
        ids.push(row.id);
      }
    });
    if (countOrdersExistInLocation) {
      this.showSnackbar(
        `${
          countOrdersExistInLocation < this.selectedOrders.length ? countOrdersExistInLocation + ' orders ' : ''
        }Inventory location assigned`,
        'info',
      );
    }
    if (ids.length) {
      this.isOrdersUpdating = true;
      this.disableGroupActions = true;
      this.orderService
        .changeOrdersLocation(ids, newLocationId)
        .pipe(
          catchError((error) => {
            this.endLocationChanged();
            this.showSnackbar(`Failed to change inventory locations`, 'danger');
            return of(error);
          }),
          takeUntil(this.destroyed$),
        )
        .subscribe((data) => {
          if (data && !data.error) {
            this.endLocationChanged();
            const succeededOrders = data.filter((x) => x.success);
            if (succeededOrders.length) {
              this.showSnackbar(
                `${
                  succeededOrders.length < data.length ? succeededOrders.length + ' orders ' : ''
                }Inventory location changed`,
                'info',
              );
              succeededOrders.forEach((succeededOrder) => {
                const row = this.selectedOrders.find((x) => x.id === succeededOrder.id);
                if (row) {
                  row.shipFromAddress = shipFromAddress;
                }
              });
            }
            data
              .filter((x) => !x.success)
              .forEach((x, index) => {
                const order = this.selectedOrders.find((order) => order.id === x.id);
                const orderNumber = order.splitted
                  ? `${order.orderNumber}${getOrderSplitIndicesString(order, 'view', true)}`
                  : order.orderNumber;
                setTimeout(() => {
                  this.showSnackbar(
                    x.error ? `${x.error} for` : `Failed to change inventory location for`,
                    'danger',
                    {
                      label: `order #${orderNumber}`,
                      variant: 'monochrome',
                    },
                    order.id,
                  );
                }, index * 200);
              });
          }
        });
    } else {
      this.isSyncingOrders = false;
    }
  }

  endLocationChanged() {
    this.isSyncingOrders = false;
    this.isOrdersUpdating = false;
    this.disableGroupActions = false;
    this.selectedOrders.forEach((row) => {
      this.setRowIsSyncing(row, false);
    });
  }

  updateOrderBoxUsingShortcut(orderIds: string[], shortcut: string): void {
    if (orderIds?.length) {
      const targetBoxId = this.boxService.getBoxByShortcut(shortcut, this.shopBoxes)?.id;
      orderIds.map((id) => {
        this.tableService.ordersInProcessMap.set(id, true);
      });
      this.refreshOrdersState(orderIds.map((id) => ({ id })) as Order[]);
      this.orderService
        .changeOrdersPackage(orderIds, targetBoxId, { emitSocketToFrontend: false })
        .pipe(takeUntil(this.destroyed$))
        .subscribe(
          () => {
            const message = getBoxUpdateSuccessMessage(orderIds.length);
            this.showSnackbar(message, 'success');
          },
          (error) => {
            const message = getBoxUpdateFailureMessage(orderIds.length);
            this.tableService.ordersInProcessMap.clear();
            this.refreshOrdersState(orderIds.map((id) => ({ id })) as Order[]);
            this.showSnackbar(message, 'danger');
          },
        );
    }
  }

  editPackageOrders(options: { shortcut?: string | null } = {}) {
    if (this.selectedOrders.length > 500) {
      this.showSnackbar('you can’t modified more than 500 orders', 'danger');
    } else if (options?.shortcut) {
      this.updateOrderBoxUsingShortcut(
        this.selectedOrders.map((order) => order.id),
        options.shortcut,
      );
    } else {
      const dialogRef = this.dialog.open(EditOrderPackageComponent, {
        backdropClass: 'global_modal_overlay_subdued',
        panelClass: 'overflow-visible-class',
        data: {
          initialWeight: { weight: 0, weightUnit: WeightUnitEnum.LB },
          shopBoxes: this.shopBoxes,
          orderIds: this.selectedOrders.map((order) => order.id),
        },
      });
      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((success) => {
          if (success) {
            this.selectedOrders = [];
          }
        });
    }
  }

  mergeOrders() {
    this.dialog
      .open(MergeOrdersComponent, {
        width: '612px',
        maxHeight: '800px',
        backdropClass: 'global_modal_overlay_subdued',
        panelClass: 'overflow-visible-class',
        data: this.selectedOrders,
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((success) => {
        if (success) {
          this.showSnackbar(`${this.selectedOrders.length} orders merged`, 'info');
        } else if (success == false) {
          this.showSnackbar('Failed to merge orders. Try again or reach out to support', 'danger');
        }
        this.selectedOrders = [];
      });
  }
  showSnackbar(message, mode: 'danger' | 'success' | 'info' | 'secondary', link?, orderId?: number) {
    const toastRef = this.toastService.show(PdqToastComponent, {
      mode,
      message,
      link,
      duration: TimeUnits._SECOND_IN_MS * 5,
    });
    if (orderId) {
      toastRef.componentInstance.onAction.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        const row = { id: orderId };
        this.onTableActivate({ row });
      });
    }
  }

  closeSidePanel(drawer) {
    drawer.close();
  }

  addTagSelectedOrders() {
    this.dialog
      .open(AddTagsComponent, {
        width: '612px',
        height: '300px',
        panelClass: 'unify-locations',
        disableClose: true,
        data: { orders: this.selectedOrders, shopId: this.shop.id },
      })
      .afterClosed();
    // .subscribe(() => {
    //   this.selectedOrders = [];
    // });
  }

  exportSelectedOrders({ option }: { option: ExportOptionType }): void {
    const dialogRef = this.dialog.open(DownloadorderdialogComponent, {
      maxWidth: '100vw',
      width: '35vw',
      panelClass: 'exportorders_popup',
      backdropClass: 'global_modal_overlay',
      data: {
        option,
        selectedOrders: this.selectedOrders,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        console.log('The dialog was closed');
      });
  }

  initTemplateDataSubscription() {
    this.templateDataChangedSubscription$ = this.templateDataChanged$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((templateData: TemplateData) => {
        if (templateData?.notVerifiedName) {
          this.addNotVerifiedFilter(templateData.notVerifiedName);
        }
      });
  }

  addNotVerifiedFilter(text: string) {
    const filterSelectOptions = this.columns.find((x) => x.type === ColumnTypes.VALIDATED_STATUS)?.filterSelectOptions;
    const columnFilter = filterSelectOptions?.find((c) => c.value === FlaggedStatuses.FLAG_NOT_VERIFIED);
    if (columnFilter) {
      columnFilter.text = text;
    } else {
      filterSelectOptions.push({
        value: FlaggedStatuses.FLAG_NOT_VERIFIED,
        text,
      });
    }
  }
}
